import React, { useMemo } from 'react';
import ListControls, { EmptyFilterState } from './ListControls';
import ContextualHelpPanel from './ContextualHelpPanel';
import {
  ALL_FILTER_VALUE,
  FORM_SORT_OPTIONS,
  FORM_STATUS_OPTIONS,
  createFormControls,
  getFormClientOptions,
  getFormListView,
  getFormTemplateOptions,
  getOptionLabel,
} from '../navigationUtils';

const FormList = ({
  forms,
  templates,
  clients,
  onSelect,
  onDelete,
  onShare,
  onSync,
  shareStatusByFormId = {},
  controls,
  onControlsChange,
  showClientFilter = true,
  helpConfig,
}) => {
  const normalizedControls = createFormControls(controls);
  const effectiveControls = showClientFilter
    ? normalizedControls
    : { ...normalizedControls, clientId: ALL_FILTER_VALUE };

  const formView = useMemo(
    () => getFormListView(forms, templates, clients, effectiveControls),
    [clients, effectiveControls, forms, templates]
  );

  const templateOptions = useMemo(
    () => getFormTemplateOptions(forms, templates, normalizedControls.templateId),
    [forms, normalizedControls.templateId, templates]
  );

  const clientOptions = useMemo(
    () => getFormClientOptions(forms, clients, normalizedControls.clientId),
    [clients, forms, normalizedControls.clientId]
  );

  const activeChips = [];

  if (normalizedControls.search.trim()) {
    activeChips.push({
      id: 'search',
      label: `Search: ${normalizedControls.search.trim()}`,
      onRemove: () => onControlsChange({ search: '' }),
    });
  }

  if (normalizedControls.status !== ALL_FILTER_VALUE) {
    activeChips.push({
      id: 'status',
      label: `Status: ${getOptionLabel(FORM_STATUS_OPTIONS, normalizedControls.status)}`,
      onRemove: () => onControlsChange({ status: ALL_FILTER_VALUE }),
    });
  }

  if (normalizedControls.templateId !== ALL_FILTER_VALUE) {
    activeChips.push({
      id: 'template',
      label: `Template: ${getOptionLabel(templateOptions, normalizedControls.templateId, 'Unknown template')}`,
      onRemove: () => onControlsChange({ templateId: ALL_FILTER_VALUE }),
    });
  }

  if (showClientFilter && normalizedControls.clientId !== ALL_FILTER_VALUE) {
    activeChips.push({
      id: 'client',
      label: `Client: ${getOptionLabel(clientOptions, normalizedControls.clientId, 'Unknown client')}`,
      onRemove: () => onControlsChange({ clientId: ALL_FILTER_VALUE }),
    });
  }

  if (normalizedControls.sortBy !== 'updated-desc') {
    activeChips.push({
      id: 'sort',
      label: `Sort: ${getOptionLabel(FORM_SORT_OPTIONS, normalizedControls.sortBy)}`,
      onRemove: () => onControlsChange({ sortBy: 'updated-desc' }),
    });
  }

  return (
    <div className="df-list-layout">
      {helpConfig && <ContextualHelpPanel {...helpConfig} />}

      {forms.length === 0 ? (
        <p style={{ color: '#666', textAlign: 'center', padding: '40px' }}>No forms found yet.</p>
      ) : (
        <>
          <ListControls
            searchPlaceholder="Search by form title, client, or template"
            searchValue={normalizedControls.search}
            onSearchChange={(search) => onControlsChange({ search })}
            filters={[
              {
                id: 'form-status-filter',
                label: 'Status',
                value: normalizedControls.status,
                options: FORM_STATUS_OPTIONS,
                onChange: (status) => onControlsChange({ status }),
              },
              {
                id: 'form-template-filter',
                label: 'Template',
                value: normalizedControls.templateId,
                options: templateOptions,
                onChange: (templateId) => onControlsChange({ templateId }),
              },
              ...(showClientFilter
                ? [
                    {
                      id: 'form-client-filter',
                      label: 'Client',
                      value: normalizedControls.clientId,
                      options: clientOptions,
                      onChange: (clientId) => onControlsChange({ clientId }),
                    },
                  ]
                : []),
            ]}
            sortValue={normalizedControls.sortBy}
            sortOptions={FORM_SORT_OPTIONS}
            onSortChange={(sortBy) => onControlsChange({ sortBy })}
            shownCount={formView.shownCount}
            totalCount={formView.totalCount}
            resultLabel="forms"
            chips={activeChips}
            onClear={() => onControlsChange(createFormControls())}
          />

          {formView.shownCount === 0 ? (
            <EmptyFilterState
              title="No forms match the current filters"
              description="Try a broader search, switch the selected filters, or clear everything and start again."
              onClear={() => onControlsChange(createFormControls())}
            />
          ) : (
            <div className="df-list-stack">
              {formView.items.map((form) => {
                const template = formView.templateMap.get(form.templateId);
                const client = formView.clientMap.get(form.clientId);
                const shareStatus = shareStatusByFormId[form.id];
                const isShared = Boolean(shareStatus?.active || form.activeShareLink);

                return (
                  <div key={form.id} className="list-item">
                    <div className="list-item-info">
                      <div className="list-item-title">{form.title || template?.name || 'Untitled Form'}</div>
                      <div className="list-item-subtitle">
                        Client: {client?.name || 'Unknown'} |
                        {' '}Template: {template?.name || 'Unknown'} |
                        {' '}Status: <span className={`badge badge-${form.status}`}>{form.status}</span> |
                        {' '}Updated: {new Date(form.updatedAt).toLocaleDateString()}
                        {isShared && (
                          <>
                            {' '}| <span className="badge badge-shared">Shared link active</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="df-action-group">
                      <button className="btn btn-secondary btn-sm" onClick={() => onSync?.(form)} disabled={shareStatus?.loading}>
                        {shareStatus?.loading ? 'Syncing...' : 'Sync'}
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => onSelect(form)}
                      >{isShared ? 'View' : 'Edit'}</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => onShare?.(form)}>
                        Share
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => onDelete(form.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FormList;
